import React, { useState } from 'react';
import { Chip, Avatar, Box, Typography, ChipProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getAvatarComponent } from '../../utils/getAvatarComponent.tsx';

// Define paths for PNG avatar images
const AVATAR_IMAGES = {
  Alpha: '/images/avatars/Alpha.png',
  Beta: '/images/avatars/Beta.png',
  Gamma: '/images/avatars/Gamma.png',
  Delta: '/images/avatars/Delta.png',
  Modelistic: '/images/avatars/Modelistic.png',
  Eterna: '/images/avatars/Eterna.png',
  MenuIcon: '/images/avatars/menu-icon.png',
  VoiceIcon: '/images/avatars/voice-icon.png'
};

export interface AvatarChipProps extends Omit<ChipProps, 'avatar'> {
  image?: string;
  name: string;
  role?: string;
  avatarColor?: string;
  avatarSize?: 'small' | 'medium' | 'large';
  variant?: 'filled' | 'outlined';
  onClick?: () => void;
  status?: 'active' | 'idle' | 'busy';
}

// Styled components for the avatar container with gradient border
const AvatarContainer = styled(Box)<{ avatarSize: string; avatarColor: string; isHovered: boolean }>(({ theme, avatarSize, avatarColor, isHovered }) => {
  const sizeMap = {
    small: 32,
    medium: 40,
    large: 48,
  };
  
  const containerSize = sizeMap[avatarSize as keyof typeof sizeMap] || 40;
  const borderWidth = avatarSize === 'small' ? 2 : 3;
  
  return {
    position: 'relative',
    width: containerSize,
    height: containerSize,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -borderWidth,
      left: -borderWidth,
      right: -borderWidth,
      bottom: -borderWidth,
      borderRadius: '50%',
      background: isHovered 
        ? `linear-gradient(135deg, ${avatarColor}, #FF6F00, #00CC99, ${theme.palette.primary.main})`
        : `linear-gradient(135deg, ${avatarColor}, ${theme.palette.primary.main})`,
      zIndex: 0,
      animation: isHovered ? 'rotate 3s linear infinite' : 'none',
    },
    '@keyframes rotate': {
      '0%': {
        transform: 'rotate(0deg)',
      },
      '100%': {
        transform: 'rotate(360deg)',
      },
    },
  };
});

const StyledAvatar = styled(Avatar)<{ avatarSize: string }>(({ theme, avatarSize }) => {
  const sizeMap = {
    small: 28,
    medium: 36,
    large: 44,
  };
  
  const innerSize = sizeMap[avatarSize as keyof typeof sizeMap] || 36;
  
  return {
    width: innerSize,
    height: innerSize,
    backgroundColor: 'rgba(26, 26, 46, 0.9)',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: avatarSize === 'small' ? '0.8rem' : avatarSize === 'large' ? '1.2rem' : '1rem',
    border: '2px solid rgba(26, 26, 46, 0.9)',
    position: 'relative',
    zIndex: 1,
  };
});

const StatusIndicator = styled(Box)<{ status: string; avatarSize: string }>(({ theme, status, avatarSize }) => {
  const sizeMap = {
    small: 8,
    medium: 10,
    large: 12,
  };
  
  const indicatorSize = sizeMap[avatarSize as keyof typeof sizeMap] || 10;
  
  let statusColor = '#00CC99'; // active (default)
  if (status === 'idle') statusColor = '#FFA500';
  if (status === 'busy') statusColor = '#FF4500';
  
  return {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: indicatorSize,
    height: indicatorSize,
    borderRadius: '50%',
    backgroundColor: statusColor,
    border: '2px solid rgba(26, 26, 46, 0.9)',
    zIndex: 2,
  };
});

const StyledChip = styled(Chip)<{ avatarSize: string; hasRole: boolean; chipColor: string }>(({ theme, avatarSize, hasRole, chipColor }) => {
  const paddingMap = {
    small: theme.spacing(0.5, 1),
    medium: theme.spacing(0.75, 1.5),
    large: theme.spacing(1, 2),
  };
  
  const heightMap = {
    small: 32,
    medium: 40,
    large: 48,
  };
  
  return {
    padding: paddingMap[avatarSize as keyof typeof paddingMap],
    height: hasRole ? 'auto' : heightMap[avatarSize as keyof typeof heightMap],
    borderRadius: theme.spacing(5),
    background: 'rgba(26, 26, 46, 0.8)',
    backdropFilter: 'blur(5px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    '& .MuiChip-label': {
      display: 'flex',
      flexDirection: hasRole ? 'column' : 'row',
      alignItems: hasRole ? 'flex-start' : 'center',
      padding: hasRole ? theme.spacing(0.5, 1) : theme.spacing(0, 1),
      color: '#fff',
      fontWeight: 500,
    },
    '&:hover': {
      background: 'rgba(26, 26, 46, 0.9)',
      boxShadow: `0 4px 20px rgba(${chipColor === '#8A2BE2' ? '138, 43, 226' : chipColor === '#00CC99' ? '0, 204, 153' : '255, 111, 0'}, 0.3)`,
    },
  };
});

const NameText = styled(Typography)<{ avatarSize: string }>(({ theme, avatarSize }) => ({
  fontSize: avatarSize === 'small' ? '0.85rem' : avatarSize === 'large' ? '1.1rem' : '1rem',
  fontWeight: 600,
  color: '#fff',
  lineHeight: 1.2,
}));

const RoleText = styled(Typography)<{ avatarSize: string }>(({ theme, avatarSize }) => ({
  fontSize: avatarSize === 'small' ? '0.7rem' : avatarSize === 'large' ? '0.9rem' : '0.8rem',
  color: 'rgba(255, 255, 255, 0.7)',
  marginTop: theme.spacing(0.25),
  lineHeight: 1.2,
}));

// Get avatar content based on image prop
const getAvatarContent = (
  image: string | undefined, 
  name: string, 
  avatarColor: string, 
  size: number
) => {
  // Check if the image is one of our PNG avatars
  if (image && AVATAR_IMAGES[image as keyof typeof AVATAR_IMAGES]) {
    // Return null so the Avatar component uses the src prop
    return null;
  }
  
  if (!image) {
    // If no image, return initials
    return null;
  }

  const avatarComponent = getAvatarComponent(image, avatarColor, size);
  
  // If it's a React component (SVG avatar), return it
  if (typeof avatarComponent !== 'string') {
    return avatarComponent;
  }
  
  // If it's a string (image path), return null so the Avatar component uses the src prop
  return null;
};

const AvatarChip: React.FC<AvatarChipProps> = ({
  image,
  name,
  role,
  avatarColor = '#8A2BE2',
  avatarSize = 'medium',
  variant = 'filled',
  onClick,
  status,
  size, // MUI Chip size
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Get initials from name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Get component size based on avatarSize
  const getComponentAvatarSize = () => {
    switch (avatarSize) {
      case 'small': return 24;
      case 'large': return 36;
      default: return 30;
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Determine if we should use a PNG image
  const isPngAvatar = image && AVATAR_IMAGES[image as keyof typeof AVATAR_IMAGES];
  
  // Determine if we should use an SVG image or initials
  const shouldUseImage = !isPngAvatar && image && typeof getAvatarComponent(image, avatarColor, getComponentAvatarSize()) === 'string';
  
  // Get the avatar content (SVG component or null for images)
  const avatarContent = getAvatarContent(image, name, avatarColor, getComponentAvatarSize());

  // Get the image source
  const imageSrc = isPngAvatar 
    ? AVATAR_IMAGES[image as keyof typeof AVATAR_IMAGES] 
    : shouldUseImage ? image : undefined;

  const avatarElement = (
    <AvatarContainer 
      avatarSize={avatarSize} 
      avatarColor={avatarColor} 
      isHovered={isHovered}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <StyledAvatar 
        src={imageSrc}
        alt={name} 
        avatarSize={avatarSize}
        sx={{ 
          width: getComponentAvatarSize(), 
          height: getComponentAvatarSize(),
          ...(imageSrc && {
            objectFit: 'cover',
            backgroundSize: 'cover'
          })
        }}
      >
        {!imageSrc ? (avatarContent || getInitials(name)) : null}
      </StyledAvatar>
      {status && <StatusIndicator status={status} avatarSize={avatarSize} />}
    </AvatarContainer>
  );

  // Render the label based on whether we have a role
  const label = role ? (
    <>
      <NameText avatarSize={avatarSize}>{name}</NameText>
      <RoleText avatarSize={avatarSize}>{role}</RoleText>
    </>
  ) : (
    <NameText avatarSize={avatarSize}>{name}</NameText>
  );

  // Use MUI size or derive from our avatarSize
  const muiSize = size || (avatarSize === 'small' ? 'small' : 'medium');

  return (
    <StyledChip
      size={muiSize}
      label={label}
      hasRole={Boolean(role)}
      chipColor={avatarColor}
      avatarSize={avatarSize}
      avatar={avatarElement}
      onClick={onClick}
      variant={variant}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    />
  );
};

export default AvatarChip; 